import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const API_BASE = API_ROOT
  ? API_ROOT.endsWith("/api") ? API_ROOT : `${API_ROOT}/api`
  : "/api";

const ALLOWED_MODULES = [4, 10];

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

function toInputDate(val) {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

const EMPTY_EDIT = {
  GHMemberID: null, MemberName: "", Mobile: "",
  NameTitleID: "", FNameTitleID: "", Gender: "", MemberTypeID: "", DepartmentID: "", Active: "Y",
  FName: "", DOB: "", PermanentAddress: "", PermanentStateID: "", PermanentCityID: "",
};

export default function FellowsList() {
  const navigate = useNavigate();
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);
  const moduleId = parseInt(localUser?.moduleId, 10);

  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");

  // dropdown masters
  const [nameTitles,   setNameTitles]   = useState([]);
  const [memberTypes,  setMemberTypes]  = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [states,       setStates]       = useState([]);
  const [cities,       setCities]       = useState([]);

  // edit modal
  const [editForm,    setEditForm]    = useState(EMPTY_EDIT);
  const [editLoading, setEditLoading] = useState(false);
  const [editMsg,     setEditMsg]     = useState({ text: "", type: "" });
  const [showModal,   setShowModal]   = useState(false);

  const fetchRows = async () => {
    try {
      setLoading(true);
      setError("");
      const res  = await fetch(`${API_BASE}/guesthouse/fellows-list?moduleId=${moduleId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to fetch fellow list");
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to fetch fellow list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ALLOWED_MODULES.includes(moduleId)) { navigate("/"); return; }
    fetchRows();
    fetch(`${API_BASE}/meta/name-titles`).then(r => r.json()).then(d => setNameTitles(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API_BASE}/meta/member-types`).then(r => r.json()).then(d => setMemberTypes(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API_BASE}/directory/departments`).then(r => r.json()).then(d => setDepartments(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API_BASE}/states`).then(r => r.json()).then(d => setStates(Array.isArray(d) ? d : [])).catch(() => {});
  }, [moduleId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // load cities whenever state changes in edit form
  useEffect(() => {
    if (!editForm.PermanentStateID) { setCities([]); return; }
    fetch(`${API_BASE}/cities?stateId=${editForm.PermanentStateID}`)
      .then(r => r.json())
      .then(d => setCities(Array.isArray(d) ? d : []))
      .catch(() => setCities([]));
  }, [editForm.PermanentStateID]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ALLOWED_MODULES.includes(moduleId)) return null;

  const openEdit = async (memberId) => {
    setEditMsg({ text: "", type: "" });
    try {
      const res  = await fetch(`${API_BASE}/guesthouse/fellow/${memberId}?moduleId=${moduleId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load member.");
      setEditForm({
        GHMemberID:       data.GHMemberID,
        MemberName:       data.MemberName        || "",
        Mobile:           data.Mobile            || "",
        NameTitleID:      data.NameTitleID   != null ? String(data.NameTitleID)   : "",
        FNameTitleID:     data.FNameTitleID  != null ? String(data.FNameTitleID)  : "",
        Gender:           data.Gender            || "",
        MemberTypeID:     data.MemberTypeID  != null ? String(data.MemberTypeID)  : "",
        DepartmentID:     data.DepartmentID  != null ? String(data.DepartmentID)  : "",
        Active:           data.Active            || "Y",
        FName:            data.FName             || "",
        DOB:              toInputDate(data.DOB),
        PermanentAddress: data.PermanentAddress  || "",
        PermanentStateID: data.PermanentStateID  != null ? String(data.PermanentStateID) : "",
        PermanentCityID:  data.PermanentCityID   != null ? String(data.PermanentCityID)  : "",
      });
      setShowModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === "PermanentStateID") next.PermanentCityID = "";
      return next;
    });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditMsg({ text: "", type: "" });
    try {
      const res  = await fetch(
        `${API_BASE}/guesthouse/fellow/${editForm.GHMemberID}?moduleId=${moduleId}`,
        {
          method:  "PUT",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({
            NameTitleID:      editForm.NameTitleID      || null,
            FNameTitleID:     editForm.FNameTitleID     || null,
            Gender:           editForm.Gender           || null,
            MemberTypeID:     editForm.MemberTypeID     || null,
            DepartmentID:     editForm.DepartmentID     || null,
            Active:           editForm.Active,
            FName:            editForm.FName            || null,
            DOB:              editForm.DOB              || null,
            PermanentAddress: editForm.PermanentAddress || null,
            PermanentStateID: editForm.PermanentStateID || null,
            PermanentCityID:  editForm.PermanentCityID  || null,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update.");
      setShowModal(false);
      fetchRows();
    } catch (err) {
      setEditMsg({ text: err.message, type: "danger" });
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <section className="container-fluid">
      <h3 className="mb-3">Fellow Record</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped table-hover table-sm align-middle" style={{ '--bs-table-hover-bg': '#aed6f1' }}>
            <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
              <tr>
                <th>SNo</th>
                <th>Name</th>
                <th>FathersName</th>
                <th>Mobile</th>
                <th>DOB</th>
                <th>Address</th>
                <th>City</th>
                <th>State</th>
                <th>Edit</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted">No records found.</td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={row.GHMemberID || index}>
                    <td>{index + 1}</td>
                    <td>{[row.NameTitle, row.MemberName].filter(Boolean).join(" ")}</td>
                    <td>{[row.FNameTitle, row.FName].filter(Boolean).join(" ")}</td>
                    <td>{row.Mobile || ""}</td>
                    <td>{formatDate(row.DOB)}</td>
                    <td>{row.PermanentAddress || ""}</td>
                    <td>{row.City || ""}</td>
                    <td>{row.State || ""}</td>
                    <td>
                      <button className="btn btn-link btn-sm p-0" onClick={() => openEdit(row.GHMemberID)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.55)" }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content" style={{ backgroundColor: "#e8e8e8" }}>
              <div className="modal-header py-2">
                <h5 className="modal-title fw-bold">Edit Fellow Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-2 small text-muted">
                  <strong>Name:</strong> {editForm.MemberName} &nbsp;|&nbsp;
                  <strong>Mobile:</strong> {editForm.Mobile}
                </div>

                {editMsg.text && (
                  <div className={`alert alert-${editMsg.type} py-1 mb-2`}>{editMsg.text}</div>
                )}

                <form onSubmit={handleEditSave}>
                  <div className="row g-2">
                    <div className="col-sm-4">
                      <label className="form-label mb-1 small">Title</label>
                      <select className="form-select form-select-sm" name="NameTitleID" value={editForm.NameTitleID} onChange={handleEditChange}>
                        <option value="">-- Select --</option>
                        {nameTitles.map(t => (
                          <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-sm-4">
                      <label className="form-label mb-1 small">Gender</label>
                      <select className="form-select form-select-sm" name="Gender" value={editForm.Gender} onChange={handleEditChange}>
                        <option value="">-- Select --</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                      </select>
                    </div>

                    <div className="col-sm-4">
                      <label className="form-label mb-1 small">Active Status</label>
                      <select className="form-select form-select-sm" name="Active" value={editForm.Active} onChange={handleEditChange}>
                        <option value="Y">Active</option>
                        <option value="N">Inactive</option>
                      </select>
                    </div>

                    <div className="col-sm-4">
                      <label className="form-label mb-1 small">Member Type</label>
                      <select className="form-select form-select-sm" name="MemberTypeID" value={editForm.MemberTypeID} onChange={handleEditChange}>
                        <option value="">-- Select --</option>
                        {memberTypes.map(t => (
                          <option key={t.MemberTypeID} value={t.MemberTypeID}>{t.MemberType}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-sm-8">
                      <label className="form-label mb-1 small">Department</label>
                      <select className="form-select form-select-sm" name="DepartmentID" value={editForm.DepartmentID} onChange={handleEditChange}>
                        <option value="">-- Select Department --</option>
                        {departments.map(d => (
                          <option key={d.DepartmentID} value={d.DepartmentID}>{d.Department}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-sm-3">
                      <label className="form-label mb-1 small">Father's Title</label>
                      <select className="form-select form-select-sm" name="FNameTitleID" value={editForm.FNameTitleID} onChange={handleEditChange}>
                        <option value="">-- Select --</option>
                        {nameTitles.map(t => (
                          <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label mb-1 small">FathersName</label>
                      <input className="form-control form-control-sm" name="FName" value={editForm.FName} onChange={handleEditChange} />
                    </div>

                    <div className="col-sm-3">
                      <label className="form-label mb-1 small">Date of Birth</label>
                      <input className="form-control form-control-sm" type="date" name="DOB" value={editForm.DOB} onChange={handleEditChange} />
                    </div>

                    <div className="col-sm-12">
                      <label className="form-label mb-1 small">Address</label>
                      <input className="form-control form-control-sm" name="PermanentAddress" value={editForm.PermanentAddress} onChange={handleEditChange} />
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label mb-1 small">State</label>
                      <select className="form-select form-select-sm" name="PermanentStateID" value={editForm.PermanentStateID} onChange={handleEditChange}>
                        <option value="">-- Select State --</option>
                        {states.map(s => (
                          <option key={s.StateID} value={s.StateID}>{s.State}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-sm-6">
                      <label className="form-label mb-1 small">City</label>
                      <select className="form-select form-select-sm" name="PermanentCityID" value={editForm.PermanentCityID} onChange={handleEditChange} disabled={!editForm.PermanentStateID}>
                        <option value="">-- Select City --</option>
                        {cities.map(c => (
                          <option key={c.CityID} value={c.CityID}>{c.City}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-3 d-flex gap-2">
                    <button className="btn btn-success btn-sm" type="submit" disabled={editLoading}>
                      {editLoading ? "Saving..." : "Save"}
                    </button>
                    <button className="btn btn-secondary btn-sm" type="button" onClick={() => setShowModal(false)}>
                      Close
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
